// src/pages/History.js
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { api } from "../lib/api";
import { useAuth } from "../auth/useAuth";
import { ethers } from "ethers";

/**
 * ---------------------------------------------------------------------
 * ChainForge — History (SaaS / Private)
 * ---------------------------------------------------------------------
 *
 * SaaS UX contract:
 * - Only the TOP HEADER (AuthBar) shows: Connect / Sign in / address / Sign out
 * - This page NEVER shows connect/sign-in buttons
 * - If unauthenticated: show one clean gate message (AuthGate)
 *
 * Data:
 * - GET /deployments?mine=true (requires JWT)
 *
 * Notes:
 * - Verification is a DEPLOYMENT concept, not a MINT concept.
 * - Mint rows never show "verified".
 * - Mint rows are enriched using matching deployment (network + address).
 */

function shorten(value) {
  if (!value || typeof value !== "string") return "-";
  if (value.length <= 12) return value;
  return `${value.slice(0, 6)}…${value.slice(-4)}`;
}

function formatSupplyDisplay(value, decimals = null, rawValue = null) {
  const hasValue = value != null && String(value).trim() !== "";
  if (hasValue) {
    const raw = String(value).trim();
    const num = Number(raw);
    if (Number.isFinite(num)) return num.toLocaleString();
    return raw;
  }

  const hasRaw = rawValue != null && String(rawValue).trim() !== "";
  if (hasRaw && decimals != null && String(decimals).trim() !== "") {
    try {
      const formatted = ethers.formatUnits(String(rawValue), Number(decimals));
      const num = Number(formatted);
      return Number.isFinite(num) ? num.toLocaleString() : formatted;
    } catch {
      return String(rawValue);
    }
  }

  return "-";
}

function safeApiError(err, fallback = "Something went wrong.") {
  return (
    err?.response?.data?.error ||
    err?.response?.data?.message ||
    err?.message ||
    fallback
  );
}

function toLower(v) {
  return String(v || "").toLowerCase();
}

function normalizeKind(v) {
  const k = String(v || "").trim().toLowerCase();
  if (k === "deployment" || k === "mint") return k;
  return "unknown";
}

function normalizeTokenType(v) {
  const t = String(v || "").trim().toUpperCase();
  if (t === "ERC20" || t === "ERC721" || t === "ERC1155") return t;
  return t || "-";
}

function normalizeNetwork(v) {
  return String(v || "-").trim().toLowerCase();
}

function normalizeAddress(rec) {
  return (
    rec?.contractAddress ||
    rec?.address ||
    rec?.result?.contractAddress ||
    rec?.result?.address ||
    ""
  );
}

function normalizeWhen(rec) {
  return rec?.confirmedAt || rec?.deployedAt || rec?.createdAt || null;
}

function compareNewestFirst(a, b) {
  const ad = normalizeWhen(a);
  const bd = normalizeWhen(b);
  const at = ad ? new Date(ad).getTime() : 0;
  const bt = bd ? new Date(bd).getTime() : 0;
  return bt - at;
}

function buildDeploymentIndex(records) {
  const map = new Map();

  for (const r of records || []) {
    const kind = normalizeKind(r?.kind);
    if (kind !== "deployment") continue;

    const network = normalizeNetwork(r?.network);
    const address = toLower(normalizeAddress(r));
    if (!network || network === "-" || !address) continue;

    map.set(`${network}:${address}`, r);
  }

  return map;
}

function formatWhen(iso) {
  if (!iso) return "-";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleString();
}

function verificationBadgeClass(status) {
  const s = String(status || "").trim().toLowerCase();

  if (s === "verified") {
    return "border-emerald-200 bg-emerald-50 text-emerald-700";
  }

  if (s === "pending" || s === "queued" || s === "submitting") {
    return "border-[#E6C978]/50 bg-[#FFF8E6] text-[#8A641E]";
  }

  if (s === "retryable" || s === "failed") {
    return "border-rose-200 bg-rose-50 text-rose-700";
  }

  return "border-slate-200 bg-slate-50 text-slate-600";
}

function kindBadgeClass(kind) {
  return kind === "deployment"
    ? "border-[#E6C978]/60 bg-[#FFF8E6] text-[#8A641E]"
    : kind === "mint"
      ? "border-slate-300 bg-slate-100 text-slate-700"
      : "border-slate-200 bg-slate-50 text-slate-600";
}

function cn(...values) {
  return values.filter(Boolean).join(" ");
}

function Surface({ className = "", children }) {
  return (
    <div
      className={cn(
        "rounded-3xl border border-slate-200/80 bg-white shadow-[0_18px_50px_rgba(15,23,42,0.06)]",
        className,
      )}
    >
      {children}
    </div>
  );
}

function InlineNotice({ title, subtitle, tone = "neutral" }) {
  const toneClass =
    tone === "warning"
      ? "border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50 text-amber-900"
      : tone === "error"
        ? "border-red-200 bg-gradient-to-r from-red-50 to-rose-50 text-red-900"
        : tone === "success"
          ? "border-emerald-200 bg-gradient-to-r from-emerald-50 to-teal-50 text-emerald-900"
          : "border-slate-200 bg-gradient-to-r from-slate-50 to-white text-slate-900";

  return (
    <div className={cn("rounded-2xl border p-4", toneClass)}>
      <div className="text-sm font-semibold">{title}</div>
      {subtitle ? (
        <div className="mt-1 text-xs leading-5 opacity-80">{subtitle}</div>
      ) : null}
    </div>
  );
}

function StatCard({ label, value, hint, tone = "neutral" }) {
  const toneClass =
    tone === "brand"
      ? "border-[#E6C978]/50 bg-gradient-to-br from-white via-[#FFF8E6] to-white"
      : tone === "success"
        ? "border-emerald-200 bg-gradient-to-br from-white via-emerald-50 to-white"
        : tone === "warning"
          ? "border-[#E6C978]/50 bg-gradient-to-br from-white via-[#FFF8E6] to-white"
          : "border-slate-200 bg-white";

  return (
    <div className={cn("rounded-3xl border p-4 shadow-sm", toneClass)}>
      <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
        {label}
      </div>
      <div className="mt-2 text-2xl font-semibold tracking-tight text-[#050713]">
        {value}
      </div>
      {hint ? (
        <p className="mt-1.5 text-xs leading-5 text-slate-500">{hint}</p>
      ) : null}
    </div>
  );
}

function FieldLabel({ children }) {
  return (
    <div className="mb-2 text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
      {children}
    </div>
  );
}

function SelectField(props) {
  return (
    <select
      {...props}
      className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#C59B45] focus:ring-4 focus:ring-[#E6C978]/20"
    />
  );
}

function SearchInput(props) {
  return (
    <input
      {...props}
      className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-[#C59B45] focus:ring-4 focus:ring-[#E6C978]/20"
    />
  );
}

function SecondaryButton({ children, className = "", ...props }) {
  return (
    <button
      {...props}
      className={cn(
        "inline-flex items-center justify-center rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 shadow-sm transition hover:border-[#E6C978] hover:bg-[#FFF8E6] disabled:cursor-not-allowed disabled:opacity-50",
        className,
      )}
    >
      {children}
    </button>
  );
}

function DarkButtonLink({ to, children }) {
  return (
    <Link
      to={to}
      className="inline-flex items-center justify-center rounded-2xl bg-[#050713] px-4 py-3 text-sm font-semibold text-white shadow-[0_14px_34px_rgba(5,7,19,0.20)] transition hover:-translate-y-0.5 hover:bg-[#111827]"
    >
      {children}
    </Link>
  );
}

export default function History() {
  const { isAuthed, restoreLoading } = useAuth();

  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errText, setErrText] = useState("");

  // UI filters
  const [filterKind, setFilterKind] = useState("all"); // all | deployment | mint
  const [filterNetwork, setFilterNetwork] = useState("all");
  const [search, setSearch] = useState("");
  const [showSensitive, setShowSensitive] = useState(false);

  /* -------------------------------------------------------------------
   * Load history (PRIVATE ONLY)
   * ----------------------------------------------------------------- */
  const loadHistory = useCallback(async () => {
    if (restoreLoading || !isAuthed) return;

    setLoading(true);
    setErrText("");

    try {
      const res = await api.get("/deployments", { params: { mine: true } });
      const list = Array.isArray(res?.data?.records) ? res.data.records : [];
      setRecords([...list].sort(compareNewestFirst));
    } catch (err) {
      if (err?.response?.status === 401) {
        setRecords([]);
        setErrText("Session expired. Please sign in again from the top bar.");
      } else {
        setRecords([]);
        setErrText(safeApiError(err, "Unable to load history."));
      }
    } finally {
      setLoading(false);
    }
  }, [isAuthed, restoreLoading]);

  useEffect(() => {
    if (restoreLoading) return;

    if (!isAuthed) {
      setRecords([]);
      setErrText("");
      setLoading(false);
      return;
    }

    loadHistory();
  }, [restoreLoading, isAuthed, loadHistory]);

  /* -------------------------------------------------------------------
   * Derived filters
   * ----------------------------------------------------------------- */
  const networks = useMemo(() => {
    const set = new Set();
    for (const r of records) {
      const n = normalizeNetwork(r?.network);
      if (n && n !== "-") set.add(n);
    }
    return ["all", ...Array.from(set).sort()];
  }, [records]);

  const deploymentIndex = useMemo(() => buildDeploymentIndex(records), [records]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();

    return (records || []).filter((r) => {
      const kind = normalizeKind(r?.kind);
      const network = normalizeNetwork(r?.network);
      const tokenType = normalizeTokenType(r?.tokenType);

      const address = normalizeAddress(r);
      const txHash = String(r?.txHash || "");

      const tokenName =
        r?.token?.tokenName ||
        r?.tokenName ||
        r?.deployment?.token?.tokenName ||
        "";

      const symbol =
        r?.token?.tokenSymbol ||
        r?.tokenSymbol ||
        r?.deployment?.token?.tokenSymbol ||
        "";

      if (filterKind !== "all" && kind !== filterKind) return false;
      if (filterNetwork !== "all" && network !== filterNetwork) return false;

      if (!q) return true;

      const haystack = [
        kind,
        network,
        tokenType,
        toLower(address),
        toLower(txHash),
        toLower(tokenName),
        toLower(symbol),
      ].join(" ");

      return haystack.includes(q);
    });
  }, [records, filterKind, filterNetwork, search]);

  /* -------------------------------------------------------------------
   * Rows (enriched + normalized for UI)
   * ----------------------------------------------------------------- */
  const rows = useMemo(() => {
    return filtered.map((r) => {
      const kind = normalizeKind(r?.kind);
      const tokenType = normalizeTokenType(r?.tokenType);
      const network = normalizeNetwork(r?.network);

      const address = normalizeAddress(r);
      const addressLower = toLower(address);

      const txHash = String(r?.txHash || "");
      const explorerUrl = String(r?.explorerUrl || r?.etherscanUrl || "");
      const explorerTxUrl = String(r?.explorerTxUrl || "");
      const download = String(r?.download || "");

      const dep = deploymentIndex.get(`${network}:${addressLower}`) || null;

      const name =
        r?.token?.tokenName ||
        r?.tokenName ||
        dep?.token?.tokenName ||
        dep?.tokenName ||
        "";

      const symbol =
        r?.token?.tokenSymbol ||
        r?.tokenSymbol ||
        dep?.token?.tokenSymbol ||
        dep?.tokenSymbol ||
        "";
      const decimals =
        r?.token?.decimals ??
        dep?.token?.decimals ??
        null;

      const initialSupplyHuman =
        r?.token?.initialSupplyHuman ??
        dep?.token?.initialSupplyHuman ??
        null;

      const initialSupply =
        r?.token?.initialSupply ??
        dep?.token?.initialSupply ??
        null;

      const tokenLabel =
        String(name || "").trim() && String(symbol || "").trim()
          ? `${String(name).trim()} (${String(symbol).trim()})`
          : String(name || "").trim() || (address ? shorten(address) : "-");

      const verificationStatus =
        kind === "deployment"
          ? String(r?.verificationStatus || "not_requested")
          : "";

      const tokenId = r?.tokenId != null ? String(r.tokenId) : "";
      const amount = r?.amount != null ? String(r.amount) : "";
      const when = normalizeWhen(r);

      const tokenDashLink =
        address && network && network !== "-"
          ? `/token/${network}/${address}?source=history`
          : null;

      return {
        kind,
        tokenType,
        network,
        address,
        txHash,
        explorerUrl,
        explorerTxUrl,
        download,
        tokenLabel,
        decimals,
        initialSupplyHuman,
        initialSupply,
        tokenDashLink,
        tokenId,
        amount,
        when,
        verificationStatus,
      };
    });
  }, [filtered, deploymentIndex]);

  const deploymentCount = useMemo(
    () => rows.filter((r) => r.kind === "deployment").length,
    [rows],
  );

  const mintCount = useMemo(
    () => rows.filter((r) => r.kind === "mint").length,
    [rows],
  );

  const verifiedCount = useMemo(
    () =>
      rows.filter(
        (r) =>
          r.kind === "deployment" &&
          String(r.verificationStatus || "").toLowerCase() === "verified",
      ).length,
    [rows],
  );

  /* -------------------------------------------------------------------
   * UI
   * ----------------------------------------------------------------- */
  return (
    <div className="space-y-6">
      {!isAuthed && (
        <InlineNotice
          title="Sign in to view private history"
          subtitle="Your deployment and mint history is private to your wallet-linked account. Connect and sign in from the top header to load workspace records."
          tone="warning"
        />
      )}
      <div className="pointer-events-none fixed inset-0 -z-10 bg-[radial-gradient(circle_at_top_left,_rgba(197,155,69,0.10),_transparent_32%),radial-gradient(circle_at_top_right,_rgba(5,7,19,0.07),_transparent_28%),linear-gradient(to_bottom,_#F7F8FA,_#F1F3F6_44%,_#F7F8FA)]" />

      <div className="relative overflow-hidden rounded-[32px] border border-[#2A2418] bg-[#050713] p-6 text-white shadow-[0_24px_70px_rgba(5,7,19,0.28)] sm:p-8">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,_rgba(197,155,69,0.24),_transparent_30%),radial-gradient(circle_at_bottom_right,_rgba(255,255,255,0.08),_transparent_32%)]" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#050713] via-[#050713]/95 to-[#111827]/90" />
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#E6C978]/60 to-transparent" />
        <div className="relative">
          <div className="flex flex-col gap-6 xl:flex-row xl:items-start xl:justify-between">
            <div className="max-w-3xl">
              <div className="inline-flex items-center rounded-full border border-[#E6C978]/25 bg-[#E6C978]/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-[#E6C978]">
                Private workspace history
              </div>

              <h1 className="mt-5 text-3xl font-semibold tracking-tight text-white sm:text-4xl">
                Review activity across your contract workspace.
              </h1>

              <p className="mt-3 max-w-2xl text-sm leading-7 text-slate-300 sm:text-base">
                Search deployments, mint actions, explorer links, bundles, and verification
                status from one organized private history view.
              </p>
            </div>

            <div className="grid w-full gap-3 sm:grid-cols-3 xl:max-w-xl">
              <div className="rounded-3xl border border-white/10 bg-white/[0.06] p-4 backdrop-blur">
                <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">
                  Deployments
                </div>
                <div className="mt-3 text-3xl font-semibold tracking-tight text-white">
                  {deploymentCount}
                </div>
                <p className="mt-2 text-xs leading-5 text-slate-400">
                  Contract launch records in view.
                </p>
              </div>

              <div className="rounded-3xl border border-white/10 bg-white/[0.06] p-4 backdrop-blur">
                <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">
                  Mint actions
                </div>
                <div className="mt-3 text-3xl font-semibold tracking-tight text-white">
                  {mintCount}
                </div>
                <p className="mt-2 text-xs leading-5 text-slate-400">
                  Token actions matched to history.
                </p>
              </div>

              <div className="rounded-3xl border border-[#E6C978]/20 bg-[#E6C978]/10 p-4 backdrop-blur">
                <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#E6C978]">
                  Verified
                </div>
                <div className="mt-3 text-3xl font-semibold tracking-tight text-white">
                  {verifiedCount}
                </div>
                <p className="mt-2 text-xs leading-5 text-slate-300">
                  Deployment records verified.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {errText ? (
        <InlineNotice
          title="Unable to load private history"
          subtitle={errText}
          tone="error"
        />
      ) : null}

      <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_220px] 2xl:grid-cols-[minmax(0,1fr)_240px]">
        <div className="space-y-6">
          <Surface className="overflow-hidden">
            <div className="border-b border-slate-200/80 px-6 py-5">
              <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                <div>
                  <div className="inline-flex rounded-full border border-[#E6C978]/50 bg-[#FFF8E6] px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-[#8A641E]">
                    Controls
                  </div>
                  <h2 className="mt-3 text-xl font-semibold tracking-tight text-[#050713]">
                    Filter history
                  </h2>
                  <p className="mt-1 max-w-2xl text-sm leading-6 text-slate-500">
                    Narrow private records by action type, network, token name, contract address,
                    or transaction hash.
                  </p>
                </div>

                <div className="flex flex-wrap gap-2">
                  <SecondaryButton onClick={loadHistory} disabled={loading}>
                    {loading ? "Refreshing…" : "Refresh"}
                  </SecondaryButton>

                  <SecondaryButton onClick={() => setShowSensitive((v) => !v)}>
                    {showSensitive ? "Hide addresses" : "Reveal addresses"}
                  </SecondaryButton>
                </div>
              </div>
            </div>

            <div className="p-4">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                <div>
                  <FieldLabel>Action</FieldLabel>
                  <SelectField
                    value={filterKind}
                    onChange={(e) => setFilterKind(e.target.value)}
                  >
                    <option value="all">All activity</option>
                    <option value="deployment">Deployments</option>
                    <option value="mint">Mint actions</option>
                  </SelectField>
                </div>

                <div>
                  <FieldLabel>Network</FieldLabel>
                  <SelectField
                    value={filterNetwork}
                    onChange={(e) => setFilterNetwork(e.target.value)}
                  >
                    {networks.map((n) => (
                      <option key={n} value={n}>
                        {n === "all" ? "All networks" : n}
                      </option>
                    ))}
                  </SelectField>
                </div>

                <div>
                  <FieldLabel>Search</FieldLabel>
                  <SearchInput
                    placeholder="Address, tx, token, symbol…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </div>
              </div>
            </div>
          </Surface>

          <Surface className="overflow-hidden border border-indigo-100/80 bg-gradient-to-br from-white via-white to-slate-50/80">
            <div className="border-b border-slate-200/80 px-6 py-5">
              <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                <div>
                  <div className="inline-flex rounded-full border border-[#E6C978]/50 bg-[#FFF8E6] px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-[#8A641E]">
                    Activity
                  </div>
                  <h2 className="mt-3 text-xl font-semibold tracking-tight text-[#050713]">
                    Timeline records
                  </h2>
                  <p className="mt-1 max-w-2xl text-sm leading-6 text-slate-500">
                    Deployment and mint activity with dashboard links, explorer references,
                    downloadable bundles, and deployment verification state.
                  </p>
                </div>

                <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
                  Showing <span className="font-semibold text-[#050713]">{rows.length}</span>{" "}
                  record{rows.length === 1 ? "" : "s"}
                </div>
              </div>
            </div>

            <div className="p-4">
              {loading ? (
                <div className="space-y-3">
                  <div className="h-16 animate-pulse rounded-2xl bg-slate-100" />
                  <div className="h-16 animate-pulse rounded-2xl bg-slate-100" />
                  <div className="h-16 animate-pulse rounded-2xl bg-slate-100" />
                </div>
              ) : !errText && rows.length === 0 ? (
                <div className="rounded-3xl border border-dashed border-slate-300 bg-slate-50 p-10 text-center">
                  <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl border border-slate-200 bg-white text-sm font-semibold text-slate-500">
                    —
                  </div>
                  <div className="mt-4 text-base font-semibold text-[#050713]">
                    No activity found
                  </div>
                  <p className="mx-auto mt-2 max-w-md text-sm leading-6 text-slate-500">
                    Try clearing filters, selecting all networks, or searching with a contract
                    address, transaction hash, token name, or symbol.
                  </p>
                </div>
              ) : !errText ? (
                <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white">
                  <table className="w-full min-w-[1180px] border-separate border-spacing-0 text-sm">
                    <thead>
                      <tr>
                        <th className="border-b border-slate-200 px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">
                          When
                        </th>
                        <th className="border-b border-slate-200 px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">
                          Kind
                        </th>
                        <th className="border-b border-slate-200 px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">
                          Token
                        </th>
                        <th className="border-b border-slate-200 px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">
                          Type
                        </th>
                        <th className="border-b border-slate-200 px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">
                          Supply
                        </th>
                        <th className="border-b border-slate-200 px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">
                          Network
                        </th>
                        <th className="border-b border-slate-200 px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">
                          Contract
                        </th>
                        <th className="border-b border-slate-200 px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">
                          Tx
                        </th>
                        <th className="border-b border-slate-200 px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">
                          Dashboard
                        </th>
                        <th className="border-b border-slate-200 px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">
                          Bundle
                        </th>
                        <th className="border-b border-slate-200 px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">
                          Verification
                        </th>
                      </tr>
                    </thead>

                    <tbody>
                      {rows.map((r) => {
                        const whenText = formatWhen(r.when);

                        const rowKey = [
                          r.kind,
                          r.network,
                          toLower(r.address),
                          toLower(r.txHash),
                          r.tokenId || "",
                          r.amount || "",
                        ].join("|");

                        return (
                          <tr key={rowKey} className="align-top transition hover:bg-[#FFF8E6]/35">
                            <td className="border-b border-slate-100 px-4 py-4 whitespace-nowrap text-slate-700">
                              {whenText}
                            </td>

                            <td className="border-b border-slate-100 px-4 py-4">
                              <span
                                className={cn(
                                  "inline-flex rounded-full border px-2.5 py-1 text-[11px] font-semibold",
                                  kindBadgeClass(r.kind),
                                )}
                              >
                                {r.kind}
                              </span>
                            </td>

                            <td className="border-b border-slate-100 px-4 py-4">
                              {r.tokenDashLink ? (
                                <Link
                                  to={r.tokenDashLink}
                                  className="font-medium text-[#8A641E] hover:text-[#050713] hover:underline"
                                  title="Open Token Dashboard"
                                >
                                  {r.tokenLabel}
                                </Link>
                              ) : (
                                <span className="text-slate-900">{r.tokenLabel}</span>
                              )}
                            </td>

                            <td className="border-b border-slate-100 px-4 py-4 text-slate-700">
                              {r.tokenType}
                            </td>

                            <td className="border-b border-slate-100 px-4 py-4 text-slate-700">
                              {r.kind === "deployment" && r.tokenType === "ERC20" ? (
                                <div className="flex flex-col">
                                  <span className="font-medium text-slate-900">
                                    {formatSupplyDisplay(r.initialSupplyHuman, r.decimals, r.initialSupply)}
                                  </span>
                                  <span className="text-xs text-slate-500">
                                    Decimals: {r.decimals ?? 18}
                                  </span>
                                </div>
                              ) : (
                                <span className="text-slate-400">-</span>
                              )}
                            </td>

                            <td className="border-b border-slate-100 px-4 py-4 text-slate-700">
                              {r.network}
                            </td>

                            <td className="border-b border-slate-100 px-4 py-4">
                              {r.explorerUrl ? (
                                <a
                                  href={r.explorerUrl}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="text-[#8A641E] hover:text-[#050713] hover:underline"
                                  title={r.address}
                                >
                                  {showSensitive ? r.address : shorten(r.address)}
                                </a>
                              ) : (
                                <span title={r.address} className="text-slate-700">
                                  {showSensitive ? r.address : shorten(r.address)}
                                </span>
                              )}
                            </td>

                            <td className="border-b border-slate-100 px-4 py-4">
                              {r.explorerTxUrl ? (
                                <a
                                  href={r.explorerTxUrl}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="text-[#8A641E] hover:text-[#050713] hover:underline"
                                  title={r.txHash}
                                >
                                  {showSensitive ? r.txHash : shorten(r.txHash)}
                                </a>
                              ) : (
                                <span title={r.txHash} className="text-slate-700">
                                  {showSensitive ? r.txHash : shorten(r.txHash)}
                                </span>
                              )}
                            </td>

                            <td className="border-b border-slate-100 px-4 py-4">
                              {r.tokenDashLink ? (
                                <Link
                                  to={r.tokenDashLink}
                                  className="inline-flex items-center rounded-full border border-[#E6C978]/60 bg-[#FFF8E6] px-2.5 py-1 text-[11px] font-semibold text-[#8A641E] transition hover:bg-[#F7E7B2]"
                                  title="Open Token Dashboard"
                                >
                                  Open
                                </Link>
                              ) : (
                                <span className="text-slate-400">-</span>
                              )}
                            </td>
                            <td className="border-b border-slate-100 px-4 py-4">
                              {r.kind === "deployment" && r.download ? (
                                <a
                                  href={r.download}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="inline-flex items-center rounded-full border border-[#E6C978]/60 bg-[#FFF8E6] px-2.5 py-1 text-[11px] font-semibold text-[#8A641E] transition hover:bg-[#F7E7B2]"
                                  title="Download deployment bundle"
                                >
                                  Download
                                </a>
                              ) : (
                                <span className="text-slate-400">-</span>
                              )}
                            </td>

                            <td className="border-b border-slate-100 px-4 py-4">
                              {r.kind === "deployment" ? (
                                <span
                                  className={cn(
                                    "inline-flex rounded-full border px-2.5 py-1 text-[11px] font-semibold",
                                    verificationBadgeClass(r.verificationStatus),
                                  )}
                                >
                                  {r.verificationStatus || "not_requested"}
                                </span>
                              ) : (
                                <span className="text-slate-400">-</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>

                  <div className="border-t border-slate-200 px-4 py-3">
                    <p className="text-xs leading-5 text-slate-500">
                      Tip: open a token dashboard to inspect contract details, balances, metadata,
                      and continue follow-up operations.
                    </p>
                  </div>
                </div>
              ) : null}
            </div>
          </Surface>
        </div>

        <div className="space-y-5">
          <StatCard
            label="Filters"
            value={[
              filterKind !== "all" ? filterKind : null,
              filterNetwork !== "all" ? filterNetwork : null,
              search.trim() ? "search" : null,
            ].filter(Boolean).length || "None"}
            hint="Applied timeline filters."
            tone="brand"
          />

          <StatCard
            label="Networks"
            value={networks.filter((n) => n !== "all").length}
            hint="Networks in this view."
            tone="neutral"
          />

          <Surface className="overflow-hidden">
            <div className="p-4">
              <div className="inline-flex rounded-full border border-[#E6C978]/50 bg-[#FFF8E6] px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-[#8A641E]">
                Guide
              </div>

              <h3 className="mt-3 text-base font-semibold tracking-tight text-[#050713]">
                Reading this history
              </h3>

              <div className="mt-3 space-y-2.5">
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3">
                  <div className="text-sm font-semibold text-[#050713]">Deployment records</div>
                  <p className="mt-1 text-xs leading-5 text-slate-500">
                    Address, bundle, explorer links, and verification.
                  </p>
                </div>

                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3">
                  <div className="text-sm font-semibold text-[#050713]">Mint records</div>
                  <p className="mt-1 text-xs leading-5 text-slate-500">
                    Show token action history. Verification belongs to deployments, not mint rows.
                  </p>
                </div>

                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3">
                  <div className="text-sm font-semibold text-[#050713]">Search</div>
                  <p className="mt-1 text-xs leading-5 text-slate-500">
                    Search token, network, address, or tx hash.
                  </p>
                </div>
              </div>
            </div>
          </Surface>

          <Surface className="overflow-hidden border-[#111827]">
            <div className="bg-[#050713] p-6 text-white">
              <div className="text-xs font-semibold uppercase tracking-[0.16em] text-[#E6C978]">
                Continue workflow
              </div>
              <div className="mt-3 text-xl font-semibold tracking-tight">
                Reopen records and continue operations
              </div>
              <p className="mt-2 text-sm leading-6 text-white/80">
                Use history records to reopen token dashboards, review deployments, and continue
                contract operations from the matching workspace.
              </p>

              <div className="mt-5 flex flex-wrap gap-3">
                <DarkButtonLink to="/deployments">
                  Open Deployments
                </DarkButtonLink>

                <Link
                  to="/operations"
                  className="inline-flex items-center justify-center rounded-2xl border border-white/15 px-4 py-3 text-sm font-semibold text-white transition hover:bg-white/10"
                >
                  Open Operations
                </Link>
              </div>
            </div>
          </Surface>
        </div>
      </div>
    </div>

  );
}